import React from "react";
import { Stack, Typography, Chip, Button, Tooltip, IconButton, Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import FlipCameraAndroidIcon from "@mui/icons-material/FlipCameraAndroid";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

interface AnalysisToolbarProps {
  isEditMode: boolean;
  onToggleEditMode: () => void;
  onFlipBoard: () => void;
  onResetBoard: () => void;
  onClearBoard: () => void;
}

export const AnalysisToolbar: React.FC<AnalysisToolbarProps> = ({
  isEditMode,
  onToggleEditMode,
  onFlipBoard,
  onResetBoard,
  onClearBoard,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", sm: "center" },
        gap: 1.5,
        mb: 2,
      }}
    >
      {/* Title & Mode Badge (stays on one line) */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0, whiteSpace: "nowrap" }}>
        <Typography variant="h6" fontWeight="bold" sx={{ whiteSpace: "nowrap" }}>
          Chessboard Analysis
        </Typography>
        <Chip
          label={isEditMode ? "Edit Mode" : "Analysis Mode"}
          color={isEditMode ? "info" : "success"}
          size="small"
          sx={{ fontWeight: "bold" }}
        />
      </Box>

      {/* Action Buttons (stays organized on one row) */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          flexShrink: 0,
          whiteSpace: "nowrap",
          width: { xs: "100%", sm: "auto" },
          justifyContent: { xs: "space-between", sm: "flex-end" },
        }}
      >
        {isEditMode && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<DeleteSweepIcon fontSize="small" />}
            onClick={onClearBoard}
            sx={{ textTransform: "none", fontWeight: 600, whiteSpace: "nowrap" }}
          >
            Clear Board
          </Button>
        )}

        <Button
          variant={isEditMode ? "contained" : "outlined"}
          color={isEditMode ? "info" : "primary"}
          size="small"
          startIcon={isEditMode ? <PlayArrowIcon fontSize="small" /> : <EditIcon fontSize="small" />}
          onClick={onToggleEditMode}
          sx={{ textTransform: "none", fontWeight: 600, whiteSpace: "nowrap" }}
        >
          {isEditMode ? "Analysis Mode" : "Edit Mode"}
        </Button>

        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title="Flip Board">
            <IconButton size="small" onClick={onFlipBoard} sx={{ color: "grey.300" }}>
              <FlipCameraAndroidIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Reset to Initial Position">
            <IconButton size="small" onClick={onResetBoard} sx={{ color: "grey.300" }}>
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Box>
  );
};

export default AnalysisToolbar;
